import { useState, useCallback, useMemo } from "react";
import { getDiagnosticsAnalyticsListAction } from "../../../../Api/BmsFaultsApi";
import {
  getTableDataFromApi,
  buildBaseApiParams,
  buildActionApiParams,
  formatCSVFileName,
  buildDateCategory as buildDateCategoryUtil,
} from "../utils/drillDownHelpers";
import {
  getTableConfigFromChartConfig,
  buildDefaultModalTitle,
  buildDefaultLevel2Title,
} from "../utils/drillDownConfigBuilder";

/**
 * Custom hook for managing chart drill-down functionality
 * Provides flexible defaults with easy overrides for chart-specific behavior
 *
 * @param {Object} params
 * @param {Date} params.startDate - Start date
 * @param {Date} params.endDate - End date
 * @param {Object} params.hierarchicalLocation - Hierarchical location filter
 * @param {Array} params.fleetIds - Selected customer (fleet) IDs. Scopes every
 *   request this hook issues, including the CSV and mail action paths. An empty
 *   or absent value sends no fleetIDs header, so an org with no customer
 *   selection keeps the exact request it had before.
 * @param {Array} params.alertCategories - Alert categories filter
 * @param {Array} params.operationalStatus - Operational status filter
 * @param {Array} params.filters - Additional filters
 * @param {Object} params.config - Configuration object with optional overrides
 * @returns {Object} Drill-down state and functions
 */
export const useChartDrillDown = ({
  startDate,
  endDate,
  hierarchicalLocation,
  fleetIds,
  onboardingLocations,
  allOnboardingLocations,
  alertCategories,
  operationalStatus,
  filters = [],
  config = {},
}) => {
  // Modal state management
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  // Build date category function (can be overridden)
  const buildDateCategory = useCallback(
    (dataPoint) => {
      if (config.buildDateCategory) {
        return config.buildDateCategory(dataPoint);
      }
      return buildDateCategoryUtil(dataPoint, startDate, endDate);
    },
    [config.buildDateCategory, startDate, endDate]
  );

  // The customer scope is content-keyed: a caller that rebuilds the fleet array
  // inline on every render would otherwise re-create globalFilters, change the
  // identity of every fetcher below, and make ChartDrillDownTable refire an
  // identical request.
  const fleetIdsKey = JSON.stringify(fleetIds || []);

  // Global filters for API calls. Every request in this hook, the on-screen
  // table and the CSV/mail actions alike, goes through this one object.
  const globalFilters = useMemo(
    () => ({
      hierarchicalLocation,
      // Stays [] when nothing is selected, and prepareHeadersForGlobalFilters
      // only sends fleetIDs for a non-empty list, so such a request is
      // unchanged from before.
      fleets: JSON.parse(fleetIdsKey),
      // The depot half of the same scope. prepareHeadersForGlobalFilters derives
      // the originLocations header by comparing these two, so dropping them
      // silently ignores a depot selection - which a BmsFaults originDepo deep
      // link really does set to a strict subset.
      onboardingLocations,
      allOnboardingLocations,
      alertCategories,
      operationalStatus
    }),
    [
      hierarchicalLocation,
      fleetIdsKey,
      onboardingLocations,
      allOnboardingLocations,
      alertCategories,
      operationalStatus
    ]
  );

  // Default level 1 groupBy
  const getDefaultLevel1GroupBy = useCallback(
    (dataPoint) => {
      if (config.getLevel1GroupBy) {
        return config.getLevel1GroupBy(dataPoint);
      }
      return ["errorDescription"];
    },
    [config.getLevel1GroupBy]
  );

  // Default level 1 select
  const getDefaultLevel1Select = useCallback(() => {
    if (config.getLevel1Select) {
      return config.getLevel1Select();
    }
    return ["GeneratedFaultsCount"];
  }, [config.getLevel1Select]);

  // Default level 1 metaInfo
  const getDefaultLevel1MetaInfo = useCallback(() => {
    if (config.getLevel1MetaInfo) {
      return config.getLevel1MetaInfo();
    }
    return ["vehicleCount"];
  }, [config.getLevel1MetaInfo]);

  // Default level 1 filters
  const getDefaultLevel1Filters = useCallback(
    (dataPoint, baseFilters) => {
      if (config.getLevel1Filters) {
        return config.getLevel1Filters(dataPoint, baseFilters);
      }
      return baseFilters;
    },
    [config.getLevel1Filters]
  );

  // Default level 2 groupBy
  const getDefaultLevel2GroupBy = useCallback(
    (level2Record) => {
      if (config.getLevel2GroupBy) {
        return config.getLevel2GroupBy(level2Record);
      }
      return ["batteryID"];
    },
    [config.getLevel2GroupBy]
  );

  // Default level 2 select
  const getDefaultLevel2Select = useCallback(() => {
    if (config.getLevel2Select) {
      return config.getLevel2Select();
    }
    return ["GeneratedFaultsCount"];
  }, [config.getLevel2Select]);

  // Default level 2 metaInfo
  const getDefaultLevel2MetaInfo = useCallback(() => {
    if (config.getLevel2MetaInfo) {
      return config.getLevel2MetaInfo();
    }
    return [
      "licensePlateNumber",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
      "pingStatus",
    ];
  }, [config.getLevel2MetaInfo]);

  // Default level 2 filters
  const getDefaultLevel2Filters = useCallback(
    (level2Record, dataPoint, baseFilters) => {
      if (config.getLevel2Filters) {
        return config.getLevel2Filters(level2Record, dataPoint, baseFilters);
      }
      const errorDescription = level2Record?.xLabel || level2Record?.faultName;
      return [
        ...baseFilters,
        { name: "errorDescription", values: [errorDescription] },
      ];
    },
    [config.getLevel2Filters]
  );

  // Level 1: Get table data
  const getTableData = useCallback(
    async (selectedDataPointParam, pageNumber, sortBy) => {
      // Allow full override
      if (config.getTableData) {
        return config.getTableData(selectedDataPointParam, pageNumber, sortBy);
      }

      const dataPoint = selectedDataPointParam || selectedDataPoint;

      if (!dataPoint || !startDate || !endDate) {
        return { data: [], total: 0 };
      }

      const groupBy = getDefaultLevel1GroupBy(dataPoint);
      const select = getDefaultLevel1Select();
      const metaInfo = getDefaultLevel1MetaInfo();
      const apiFilters = [...filters];
      const filtersWithDefaults = getDefaultLevel1Filters(dataPoint, apiFilters);

      const baseApiParams = buildBaseApiParams(
        dataPoint,
        startDate,
        endDate,
        groupBy,
        select,
        metaInfo,
        filtersWithDefaults,
        { pageNum: pageNumber, pageCount: 10 }
      );

      if (sortBy && sortBy.length > 0) {
        baseApiParams.sortBy = JSON.stringify([...sortBy]);
      }

      const { data: transformedData, total } = await getTableDataFromApi(
        globalFilters,
        baseApiParams
      );

      return { data: transformedData, total };
    },
    [
      config.getTableData,
      selectedDataPoint,
      startDate,
      endDate,
      filters,
      globalFilters,
      getDefaultLevel1GroupBy,
      getDefaultLevel1Select,
      getDefaultLevel1MetaInfo,
      getDefaultLevel1Filters,
    ]
  );

  // Level 1: Apply table action
  const applyTableAction = useCallback(
    async (rowSelectionKeys, actionType, selectAll, params = {}) => {
      // Allow full override
      if (config.applyTableAction) {
        return config.applyTableAction(
          rowSelectionKeys,
          actionType,
          selectAll,
          params
        );
      }

      if (!selectedDataPoint || !startDate || !endDate) {
        return actionType === "download" ? [] : null;
      }

      const groupBy = getDefaultLevel1GroupBy(selectedDataPoint);
      const select = getDefaultLevel1Select();
      const metaInfo = getDefaultLevel1MetaInfo();
      const apiFilters = [...filters];
      const filtersWithDefaults = getDefaultLevel1Filters(
        selectedDataPoint,
        apiFilters
      );

      const baseApiParams = buildBaseApiParams(
        selectedDataPoint,
        startDate,
        endDate,
        groupBy,
        select,
        metaInfo,
        filtersWithDefaults
      );

      const apiParams = buildActionApiParams(
        baseApiParams,
        actionType,
        rowSelectionKeys,
        selectAll,
        params
      );

      const res = await getDiagnosticsAnalyticsListAction(globalFilters, apiParams);
      if (res && res.responseStatus.code === 200) {
        return res.response.data || (actionType === "download" ? [] : null);
      }
      return actionType === "download" ? [] : null;
    },
    [
      config.applyTableAction,
      selectedDataPoint,
      startDate,
      endDate,
      filters,
      globalFilters,
      getDefaultLevel1GroupBy,
      getDefaultLevel1Select,
      getDefaultLevel1MetaInfo,
      getDefaultLevel1Filters,
    ]
  );

  // Level 1: Get CSV filename
  const getCSVFileName = useCallback(() => {
    // Allow full override
    if (config.getCSVFileName) {
      const result = config.getCSVFileName(selectedDataPoint);
      if (result) return result;
    }

    const prefix = config.csvFileNamePrefix || "BmsFaults_Chart";
    const metricName = selectedDataPoint?.metricName || "Faults";
    const parts = [metricName];

    return formatCSVFileName(
      prefix,
      parts,
      selectedDataPoint?.startDate || startDate,
      selectedDataPoint?.endDate || endDate
    );
  }, [config.getCSVFileName, config.csvFileNamePrefix, selectedDataPoint, startDate, endDate]);

  // Level 1: Get table config
  const getTableConfig = useCallback(
    (level = 1, dataPoint = null) => {
      // Allow full override
      if (config.getTableConfig) {
        return config.getTableConfig(level, dataPoint || selectedDataPoint);
      }

      if (config.chartType) {
        return getTableConfigFromChartConfig(
          config.chartType,
          level,
          dataPoint || selectedDataPoint
        );
      }

      return null;
    },
    [config.getTableConfig, config.chartType, selectedDataPoint]
  );

  // Level 1: Get modal title
  const getModalTitle = useCallback(
    (dataPoint) => {
      // Allow full override
      if (config.getModalTitle) {
        return config.getModalTitle(dataPoint || selectedDataPoint);
      }

      if (config.chartType) {
        const chartConfig = require("../chartConfig").CHART_CONFIG[config.chartType];
        const chartTitle = chartConfig?.title || "Chart Details";
        return buildDefaultModalTitle(
          chartTitle,
          dataPoint || selectedDataPoint,
          buildDateCategory
        );
      }

      return buildDefaultModalTitle(
        "Chart Details",
        dataPoint || selectedDataPoint,
        buildDateCategory
      );
    },
    [config.getModalTitle, config.chartType, selectedDataPoint, buildDateCategory]
  );

  // Level 2: Get table data
  const level2GetTableData = useCallback(
    async (level2Record, pageNumber, sortBy) => {
      // Allow full override
      if (config.level2GetTableData) {
        return config.level2GetTableData(level2Record, pageNumber, sortBy);
      }

      if (!selectedDataPoint || !startDate || !endDate || !level2Record) {
        return { data: [], total: 0 };
      }

      const groupBy = getDefaultLevel2GroupBy(level2Record);
      const select = getDefaultLevel2Select();
      const metaInfo = getDefaultLevel2MetaInfo();
      const apiFilters = [...filters];
      const filtersWithDefaults = getDefaultLevel2Filters(
        level2Record,
        selectedDataPoint,
        apiFilters
      );

      const baseApiParams = buildBaseApiParams(
        selectedDataPoint,
        startDate,
        endDate,
        groupBy,
        select,
        metaInfo,
        filtersWithDefaults,
        { pageNum: pageNumber, pageCount: 10 }
      );

      if (sortBy && sortBy.length > 0) {
        baseApiParams.sortBy = JSON.stringify([...sortBy]);
      }

      const { data: transformedData, total } = await getTableDataFromApi(
        globalFilters,
        baseApiParams
      );

      return { data: transformedData, total };
    },
    [
      config.level2GetTableData,
      selectedDataPoint,
      startDate,
      endDate,
      filters,
      globalFilters,
      getDefaultLevel2GroupBy,
      getDefaultLevel2Select,
      getDefaultLevel2MetaInfo,
      getDefaultLevel2Filters,
    ]
  );

  // Level 2: Apply table action
  const level2ApplyTableAction = useCallback(
    async (rowSelectionKeys, actionType, selectAll, params = {}, level2Record) => {
      // Allow full override
      if (config.level2ApplyTableAction) {
        return config.level2ApplyTableAction(
          rowSelectionKeys,
          actionType,
          selectAll,
          params,
          level2Record
        );
      }

      if (!selectedDataPoint || !startDate || !endDate || !level2Record) {
        return actionType === "download" ? [] : null;
      }

      const groupBy = getDefaultLevel2GroupBy(level2Record);
      const select = getDefaultLevel2Select();
      const metaInfo = getDefaultLevel2MetaInfo();
      const apiFilters = [...filters];
      const filtersWithDefaults = getDefaultLevel2Filters(
        level2Record,
        selectedDataPoint,
        apiFilters
      );

      const baseApiParams = buildBaseApiParams(
        selectedDataPoint,
        startDate,
        endDate,
        groupBy,
        select,
        metaInfo,
        filtersWithDefaults
      );

      const apiParams = buildActionApiParams(
        baseApiParams,
        actionType,
        rowSelectionKeys,
        selectAll,
        params
      );

      const res = await getDiagnosticsAnalyticsListAction(globalFilters, apiParams);
      if (res && res.responseStatus.code === 200) {
        return res.response.data || (actionType === "download" ? [] : null);
      }
      return actionType === "download" ? [] : null;
    },
    [
      config.level2ApplyTableAction,
      selectedDataPoint,
      startDate,
      endDate,
      filters,
      globalFilters,
      getDefaultLevel2GroupBy,
      getDefaultLevel2Select,
      getDefaultLevel2MetaInfo,
      getDefaultLevel2Filters,
    ]
  );

  // Level 2: Get CSV filename
  const level2GetCSVFileName = useCallback(
    (level2Record) => {
      // Allow full override
      if (config.level2GetCSVFileName) {
        const result = config.level2GetCSVFileName(level2Record, selectedDataPoint);
        if (result) return result;
      }

      const prefix = config.csvFileNamePrefix || "BmsFaults_Vehicles";
      const faultName = level2Record?.xLabel || level2Record?.faultName || "Fault";
      const metricName = selectedDataPoint?.metricName || "Faults";
      const parts = [faultName, metricName];

      return formatCSVFileName(
        prefix,
        parts,
        selectedDataPoint?.startDate || startDate,
        selectedDataPoint?.endDate || endDate
      );
    },
    [
      config.level2GetCSVFileName,
      config.csvFileNamePrefix,
      selectedDataPoint,
      startDate,
      endDate,
    ]
  );

  // Level 2: Get title
  const level2Title = useCallback(
    (level2Record) => {
      // Allow full override
      if (config.level2Title) {
        return config.level2Title(level2Record);
      }

      return buildDefaultLevel2Title(level2Record);
    },
    [config.level2Title]
  );

  // Handlers
  const handleDataPointClick = useCallback((clickedData) => {
    setSelectedDataPoint(clickedData);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDataPoint(null);
  }, []);

  return {
    // State
    modalOpen,
    selectedDataPoint,
    setSelectedDataPoint, // Expose setter for flexibility

    // Handlers
    handleDataPointClick,
    handleModalClose,

    // Level 1 functions
    getTableData,
    applyTableAction,
    getCSVFileName,
    getTableConfig,
    getModalTitle,

    // Level 2 functions
    level2GetTableData,
    level2ApplyTableAction,
    level2GetCSVFileName,
    level2Title,

    // Utilities
    buildDateCategory,
  };
};
